import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api';
import { useApp } from '../store';
import { useLang, LangToggle } from '../i18n';

const GOLD = '#F5C518';
const GREEN = '#2ECC71';
const RED   = '#E8453C';

function fmt(n) {
  return Number(n || 0).toLocaleString();
}

export default function Invite() {
  const nav = useNavigate();
  const { refreshUser } = useApp();
  const { lang } = useLang();
  const W = (zh, en) => lang === 'zh' ? zh : en;
  const [info, setInfo]         = useState(null);
  const [copied, setCopied]     = useState(false);
  const [loading, setLoading]   = useState(true);
  const [claiming, setClaiming] = useState({}); // { [inviteeId]: true }
  const [claimAll, setClaimAll] = useState(false);
  const [msg, setMsg]           = useState(null); // { type:'ok'|'err', text }

  const load = useCallback(async () => {
    try {
      const d = await API.get('/api/invite/info');
      setInfo(d);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const inviteLink = info
    ? `${window.location.origin}/register?invite=${info.inviteCode}`
    : '';

  function copyLink() {
    navigator.clipboard?.writeText(inviteLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function showMsg(type, text) {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 3000);
  }

  // 领取单个好友奖励
  async function claimOne(inviteeId) {
    if (claiming[inviteeId]) return;
    setClaiming(c => ({ ...c, [inviteeId]: true }));
    try {
      const r = await API.post('/api/invite/claim', { inviteeId });
      showMsg('ok', W(`成功领取 +${fmt(r.claimedCards ?? r.claimed)} 张房卡`, `Claimed +${fmt(r.claimedCards ?? r.claimed)} Room Card`));
      await load();
      await refreshUser();
    } catch (e) {
      showMsg('err', e?.message || '领取失败');
    } finally {
      setClaiming(c => ({ ...c, [inviteeId]: false }));
    }
  }

  // 一键领取全部
  async function claimAllPending() {
    if (claimAll) return;
    setClaimAll(true);
    try {
      const r = await API.post('/api/invite/claim', { all: true });
      showMsg('ok', W(`全部领取成功！+${fmt(r.claimedCards ?? r.claimed)} 张房卡已到账`, `Claimed all: +${fmt(r.claimedCards ?? r.claimed)} Room Cards`));
      await load();
      await refreshUser();
    } catch (e) {
      showMsg('err', e?.message || '领取失败');
    } finally {
      setClaimAll(false);
    }
  }

  const totalPending = info?.totalPendingCards ?? info?.totalPending ?? 0;
  const totalClaimed = info?.totalClaimedCards ?? info?.totalClaimed ?? 0;

  const S = `
    *{box-sizing:border-box;margin:0;padding:0;-webkit-tap-highlight-color:transparent;}
    .inv-root{min-height:100vh;min-height:100dvh;
      background:linear-gradient(180deg,#0B1E4A 0%,#0D2B6B 50%,#091840 100%);
      background-attachment:fixed;padding:0 0 60px;}
    .inv-header{display:flex;align-items:center;gap:14px;padding:52px 20px 20px;}
    .inv-back{background:rgba(46,127,232,0.15);border:1px solid rgba(46,127,232,0.3);
      color:#8BA8D4;font-size:18px;cursor:pointer;line-height:1;padding:8px 12px;border-radius:10px;}
    .inv-title{font-size:20px;font-weight:800;color:#fff;}
    .inv-body{padding:0 16px;}
    .inv-card{background:#0F2554;border-radius:18px;padding:20px;margin-bottom:14px;
      border:2px solid ${GOLD}55;box-shadow:inset 0 1px 0 rgba(255,255,255,0.08),0 0 20px ${GOLD}15;}
    .inv-card-plain{background:#0F2554;border-radius:18px;padding:18px 20px;margin-bottom:14px;
      border:1px solid rgba(46,127,232,0.3);box-shadow:inset 0 1px 0 rgba(255,255,255,0.06);}
    .inv-label{font-size:11px;color:#8BA8D4;letter-spacing:1px;margin-bottom:6px;text-transform:uppercase;}
    .inv-code{font-family:'Share Tech Mono',monospace;font-size:32px;font-weight:900;
      color:${GOLD};letter-spacing:6px;text-shadow:0 0 20px ${GOLD}66;}
    .inv-link-text{font-size:12px;color:#8BA8D4;word-break:break-all;line-height:1.6;margin-bottom:12px;}
    .btn-gold{width:100%;padding:13px;border-radius:12px;border:none;cursor:pointer;
      font-size:14px;font-weight:800;
      background:linear-gradient(180deg,#FFD700 0%,#E6A800 100%);color:#000;
      box-shadow:0 4px 0 #8B6200;}
    .btn-gold:active{transform:translateY(3px);box-shadow:0 1px 0 #8B6200;}
    .btn-gold:disabled{opacity:0.45;cursor:not-allowed;transform:none;box-shadow:none;}
    .btn-green{padding:8px 16px;border-radius:10px;border:none;cursor:pointer;
      font-size:13px;font-weight:800;white-space:nowrap;
      background:linear-gradient(180deg,#2ECC71 0%,#1aaa58 100%);color:#fff;
      box-shadow:0 3px 0 #0d7a3e;}
    .btn-green:active{transform:translateY(2px);box-shadow:0 1px 0 #0d7a3e;}
    .btn-green:disabled{opacity:0.45;cursor:not-allowed;transform:none;box-shadow:none;}
    .stats-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:14px;}
    .stat-box{background:#0F2554;border-radius:16px;padding:14px 10px;text-align:center;
      border:1px solid rgba(46,127,232,0.3);}
    .stat-num{font-family:'Share Tech Mono',monospace;font-size:22px;font-weight:900;color:#fff;margin-bottom:3px;}
    .stat-lbl{font-size:10px;color:#8BA8D4;letter-spacing:0.5px;}
    .section-title{font-size:14px;font-weight:700;color:#fff;margin-bottom:10px;letter-spacing:0.5px;}
    .friend-row{background:#0F2554;border-radius:14px;padding:14px 16px;margin-bottom:8px;
      border:1px solid rgba(46,127,232,0.2);display:flex;align-items:center;justify-content:space-between;gap:12px;}
    .friend-row.has-pending{border-color:${GOLD}55;background:#131f47;}
    .friend-name{font-size:14px;color:#fff;font-weight:700;margin-bottom:3px;}
    .friend-meta{font-size:11px;color:#4A6A9A;}
    .friend-pts{text-align:right;}
    .pending-amt{font-family:'Share Tech Mono',monospace;font-size:15px;font-weight:900;color:${GOLD};}
    .claimed-amt{font-size:11px;color:#4A6A9A;margin-top:2px;}
    .msg-ok{padding:11px 16px;border-radius:12px;font-size:13px;font-weight:600;margin-bottom:14px;
      background:rgba(46,204,113,0.12);color:${GREEN};border:1px solid rgba(46,204,113,0.2);}
    .msg-err{padding:11px 16px;border-radius:12px;font-size:13px;font-weight:600;margin-bottom:14px;
      background:rgba(232,69,60,0.12);color:${RED};border:1px solid rgba(232,69,60,0.2);}
    .rule-item{display:flex;gap:12px;align-items:flex-start;margin-bottom:12px;}
    .rule-item:last-child{margin-bottom:0;}
    .rule-num{width:24px;height:24px;border-radius:50%;flex-shrink:0;margin-top:1px;
      background:linear-gradient(180deg,#3A8EF6,#1A5FBF);
      color:#fff;font-size:12px;font-weight:800;display:flex;align-items:center;justify-content:center;
      box-shadow:0 2px 0 #0F3A80;}
    .rule-text{font-size:13px;color:#8BA8D4;line-height:1.6;}
    .rule-text strong{color:#fff;}
    .claim-all-banner{background:linear-gradient(135deg,#1a3a1a,#0d2b0d);border-radius:16px;
      padding:16px 18px;margin-bottom:14px;border:1px solid ${GREEN}44;
      display:flex;align-items:center;justify-content:space-between;gap:12px;}
    .empty-tip{text-align:center;color:#4A6A9A;font-size:13px;padding:24px 0;}
  `;

  return (
    <div className="inv-root">
      <style>{S}</style>

      <div className="inv-header">
        <button className="inv-back" onClick={() => nav('/lobby')}>&#8592;</button>
        <div className="inv-title">{W('邀请好友','Invite Friends')}</div>
        <LangToggle />
      </div>

      <div className="inv-body">

        {/* 消息提示 */}
        {msg && (
          <div className={msg.type === 'ok' ? 'msg-ok' : 'msg-err'}>{msg.text}</div>
        )}

        {loading ? (
          <div style={{textAlign:'center',color:'#4A6A9A',padding:60,fontFamily:'Share Tech Mono'}}>{W('加载中...','Loading...')}</div>
        ) : (
          <>
            {/* 邀请码 */}
            <div className="inv-card">
              <div className="inv-label">YOUR INVITE CODE</div>
              <div className="inv-code">{info?.inviteCode || '------'}</div>
            </div>

            <div className="inv-card-plain">
              <div className="inv-label" style={{marginBottom:8}}>{W('邀请链接','Invite Link')}</div>
              <div className="inv-link-text">{inviteLink}</div>
              <button className="btn-gold" onClick={copyLink}>
                {copied ? W('✓ 已复制','✓ Copied') : W('复制邀请链接','Copy Invite Link')}
              </button>
            </div>

            {/* 统计 */}
            <div className="stats-grid">
              <div className="stat-box">
                <div className="stat-num" style={{color:GOLD}}>{fmt(info?.inviteCount)}</div>
                <div className="stat-lbl">{W('已邀请','Invited')}</div>
              </div>
              <div className="stat-box">
                <div className="stat-num" style={{color:totalPending>0?GOLD:'#4A6A9A'}}>
                  {fmt(totalPending)}
                </div>
                <div className="stat-lbl">{W('待领房卡','Pending Cards')}</div>
              </div>
              <div className="stat-box">
                <div className="stat-num" style={{color:GREEN}}>{fmt(totalClaimed)}</div>
                <div className="stat-lbl">{W('已领房卡','Claimed Cards')}</div>
              </div>
            </div>

            {/* 一键领取全部 banner */}
            {totalPending > 0 && (
              <div className="claim-all-banner">
                <div>
                  <div style={{fontSize:13,color:'#8BA8D4',marginBottom:3}}>{W('全部待领取房卡','All Pending Cards')}</div>
                  <div style={{fontFamily:'Share Tech Mono',fontSize:22,fontWeight:900,color:GOLD}}>
                    +{fmt(totalPending)} {W('张房卡','Cards')}
                  </div>
                </div>
                <button className="btn-green" style={{padding:'12px 20px',fontSize:14}}
                  onClick={claimAllPending} disabled={claimAll}>
                  {claimAll ? W('领取中...','Claiming...') : W('一键全领','Claim All')}
                </button>
              </div>
            )}

            {/* 好友列表 */}
            <div className="section-title">{`${W('好友列表（','Friends (')}${info?.list?.length || 0}${W(' 人）',')')}`}</div>

            {!info?.list?.length ? (
              <div className="empty-tip">{W('暂无邀请记录，分享链接邀请好友吧','No invites yet. Share your link!')}</div>
            ) : (
              info.list.map((row) => {
                const pendingCards = row.pendingCards ?? row.pendingPts ?? 0;
                const claimedCards = row.claimedCards ?? row.claimedPts ?? 0;
                const hasPending = pendingCards > 0;
                return (
                  <div key={row.inviteeId} className={`friend-row${hasPending ? ' has-pending' : ''}`}>
                    <div style={{flex:1,minWidth:0}}>
                      <div className="friend-name">{row.nickname}</div>
                      <div className="friend-meta">
                        {new Date(row.joinedAt).toLocaleDateString('zh-CN')} 加入
                        {claimedCards > 0 && ` · ${W('已领', 'Claimed')} ${fmt(claimedCards)} ${W('张房卡', 'Cards')}`}
                      </div>
                    </div>
                    <div className="friend-pts">
                      {hasPending ? (
                        <>
                          <div className="pending-amt">+{fmt(pendingCards)}</div>
                          <div className="claimed-amt">{W('待领房卡','Pending Card')}</div>
                        </>
                      ) : (
                        <div style={{fontSize:12,color:'#4A6A9A'}}>{W('已领完','Claimed')}</div>
                      )}
                    </div>
                    {hasPending && (
                      <button
                        className="btn-green"
                        onClick={() => claimOne(row.inviteeId)}
                        disabled={!!claiming[row.inviteeId]}>
                        {claiming[row.inviteeId] ? '...' : W('领取','Claim')}
                      </button>
                    )}
                  </div>
                );
              })
            )}

            <div className="section-title" style={{marginTop:20}}>{W('奖励规则','Reward Rules')}</div>
            <div className="inv-card-plain">
              {(lang==='en' ? [
                [<>Friend registers → earn <strong>1 Room Card</strong></>, 'Claim manually when ready'],
                ['Invite rewards do not include COIN, USDT, or rebates', null],
                ['Pending cards never expire — credited instantly on claim', null],
              ] : [
                [<>好友注册成功，获得 <strong>1 张待领取房卡</strong></>, '需手动点击领取'],
                ['邀请奖励不再包含 COIN、USDT 或返利', null],
                ['待领房卡不会过期，点击领取后立即到账', null],
              ]).map(([a, b], i) => (
                <div key={i} className="rule-item">
                  <div className="rule-num">{i+1}</div>
                  <div className="rule-text">{a}{b && <><br/><span style={{color:'#4A6A9A',fontSize:11}}>{b}</span></>}</div>
                </div>
              ))}
            </div>

          </>
        )}
      </div>
    </div>
  );
}
